/** @typedef {{ type: 'paragraph', text: string }} ParagraphBlock */
/** @typedef {{ type: 'h1'|'h2'|'h3'|'h4', text: string }} HeadingBlock */
/** @typedef {{ type: 'bullet', text: string }} BulletBlock */
/** @typedef {{ type: 'numbered', text: string }} NumberedBlock */
/** @typedef {{ type: 'todo', checked: boolean, text: string }} TodoBlock */
/** @typedef {{ type: 'toggle', title: string, body: string }} ToggleBlock */
/** @typedef {{ type: 'quote', text: string }} QuoteBlock */
/** @typedef {{ type: 'callout', text: string }} CalloutBlock */
/** @typedef {{ type: 'divider' }} DividerBlock */
/** @typedef {{ type: 'image', fileId: string, alt?: string }} ImageBlock */

/** @typedef {ParagraphBlock|HeadingBlock|BulletBlock|NumberedBlock|TodoBlock|ToggleBlock|QuoteBlock|CalloutBlock|DividerBlock|ImageBlock} NoteBlock */

export function createEmptyBlock(type) {
  switch (type) {
    case "h1":
    case "h2":
    case "h3":
    case "h4":
      return { type, text: "" };
    case "bullet":
    case "numbered":
    case "quote":
    case "callout":
      return { type, text: "" };
    case "todo":
      return { type: "todo", checked: false, text: "" };
    case "toggle":
      return { type: "toggle", title: "", body: "" };
    case "divider":
      return { type: "divider" };
    case "image":
      return { type: "image", fileId: "", alt: "" };
    default:
      return { type: "paragraph", text: "" };
  }
}

export function isEmptyBlock(block) {
  if (!block) return true;
  switch (block.type) {
    case "divider":
      return false;
    case "image":
      return !block.fileId;
    case "todo":
      return !block.text?.trim();
    case "toggle":
      return !block.title?.trim() && !block.body?.trim();
    default:
      return !block.text?.trim();
  }
}

export function isNoteEditorEmpty(blocks) {
  if (!blocks?.length) return true;
  return blocks.every(isEmptyBlock);
}

export function blockPlainText(block) {
  if (!block) return "";
  switch (block.type) {
    case "divider":
      return "";
    case "image":
      return block.alt || "Image";
    case "todo":
      return block.text || "";
    case "toggle":
      return [block.title, block.body].filter(Boolean).join(" ");
    default:
      return block.text || "";
  }
}

function blockInlineText(block) {
  if (!block) return "";
  if (block.type === "toggle") return block.title || "";
  if (block.type === "divider") return "";
  return block.text || "";
}

/** Convert an existing block to another format, preserving text where possible. */
export function convertBlockType(block, type) {
  if (!block) return createEmptyBlock(type === "divider" ? "paragraph" : type);
  if (block.type === type && type !== "divider") return block;

  const text = blockInlineText(block);

  switch (type) {
    case "paragraph":
      if (block.type === "toggle") {
        return { type: "paragraph", text: [block.title, block.body].filter(Boolean).join("\n").trim() };
      }
      if (block.type === "divider") return { type: "paragraph", text: "" };
      return { type: "paragraph", text: block.text ?? text };
    case "todo":
      return {
        type: "todo",
        checked: block.type === "todo" ? block.checked : false,
        text: block.type === "toggle" ? block.title || "" : block.text ?? text,
      };
    case "toggle":
      return {
        type: "toggle",
        title: block.type === "toggle" ? block.title || "" : block.text ?? text,
        body: block.type === "toggle" ? block.body || "" : "",
      };
    case "divider":
      return { type: "divider" };
    case "h1":
    case "h2":
    case "h3":
    case "h4":
    case "bullet":
    case "numbered":
    case "quote":
    case "callout":
      return { type, text: block.type === "toggle" ? block.title || text : block.text ?? text };
    default:
      return { type: "paragraph", text };
  }
}

export function parseNoteContent(markdown) {
  const raw = (markdown || "").replace(/\r\n/g, "\n");
  if (!raw.trim()) return [{ type: "paragraph", text: "" }];

  const lines = raw.split("\n");
  /** @type {NoteBlock[]} */
  const blocks = [];
  let i = 0;

  const pushParagraph = (text) => {
    if (text.trim() || blocks.length === 0) blocks.push({ type: "paragraph", text });
  };

  while (i < lines.length) {
    const line = lines[i];

    if (line.trim() === "---") {
      blocks.push({ type: "divider" });
      i += 1;
      continue;
    }

    const imageMatch = line.match(/^!\[(.*?)\]\(taistu-file:([^)]+)\)$/);
    if (imageMatch) {
      blocks.push({ type: "image", alt: imageMatch[1] || "", fileId: imageMatch[2] });
      i += 1;
      continue;
    }

    if (line.startsWith("#### ")) {
      blocks.push({ type: "h4", text: line.slice(5) });
      i += 1;
      continue;
    }
    if (line.startsWith("### ")) {
      blocks.push({ type: "h3", text: line.slice(4) });
      i += 1;
      continue;
    }
    if (line.startsWith("## ")) {
      blocks.push({ type: "h2", text: line.slice(3) });
      i += 1;
      continue;
    }
    if (line.startsWith("# ")) {
      blocks.push({ type: "h1", text: line.slice(2) });
      i += 1;
      continue;
    }

    const calloutMatch = line.match(/^> \[!note\]\s?(.*)$/i);
    if (calloutMatch) {
      const bodyLines = [calloutMatch[1] || ""];
      i += 1;
      while (i < lines.length && lines[i].startsWith("> ") && !lines[i].match(/^> \[!note\]/i)) {
        bodyLines.push(lines[i].replace(/^>\s?/, ""));
        i += 1;
      }
      blocks.push({ type: "callout", text: bodyLines.join("\n").trim() });
      continue;
    }

    if (line.startsWith("> ") && !/^> \[!note\]/i.test(line)) {
      const bodyLines = [line.slice(2)];
      i += 1;
      while (i < lines.length && lines[i].startsWith("> ") && !/^> \[!note\]/i.test(lines[i])) {
        bodyLines.push(lines[i].slice(2));
        i += 1;
      }
      blocks.push({ type: "quote", text: bodyLines.join("\n") });
      continue;
    }

    const todoMatch = line.match(/^- \[( |x|X)\]\s?(.*)$/);
    if (todoMatch) {
      blocks.push({
        type: "todo",
        checked: todoMatch[1].toLowerCase() === "x",
        text: todoMatch[2] || "",
      });
      i += 1;
      continue;
    }

    if (line.startsWith("▸ ")) {
      const title = line.slice(2);
      const bodyLines = [];
      i += 1;
      while (i < lines.length && (/^\t/.test(lines[i]) || /^  /.test(lines[i]))) {
        bodyLines.push(lines[i].replace(/^\t|^  /, ""));
        i += 1;
      }
      blocks.push({ type: "toggle", title, body: bodyLines.join("\n") });
      continue;
    }

    const numberedMatch = line.match(/^(\d+)\.\s+(.*)$/);
    if (numberedMatch) {
      blocks.push({ type: "numbered", text: numberedMatch[2] });
      i += 1;
      continue;
    }

    if (line.startsWith("- ")) {
      blocks.push({ type: "bullet", text: line.slice(2) });
      i += 1;
      continue;
    }

    if (line.trim() === "") {
      i += 1;
      continue;
    }

    pushParagraph(line);
    i += 1;
  }

  return blocks.length ? blocks : [{ type: "paragraph", text: "" }];
}

export function serializeNoteContent(blocks) {
  if (!blocks?.length) return "";

  return blocks
    .map((block) => {
      switch (block.type) {
        case "h1":
          return `# ${block.text || ""}`;
        case "h2":
          return `## ${block.text || ""}`;
        case "h3":
          return `### ${block.text || ""}`;
        case "h4":
          return `#### ${block.text || ""}`;
        case "bullet":
          return `- ${block.text || ""}`;
        case "numbered":
          return `1. ${block.text || ""}`;
        case "todo":
          return `- [${block.checked ? "x" : " "}] ${block.text || ""}`;
        case "toggle": {
          const title = block.title || "";
          const body = (block.body || "")
            .split("\n")
            .map((l) => `\t${l}`)
            .join("\n");
          return body ? `▸ ${title}\n${body}` : `▸ ${title}`;
        }
        case "quote":
          return (block.text || "")
            .split("\n")
            .map((l) => `> ${l}`)
            .join("\n");
        case "callout":
          return (block.text || "")
            .split("\n")
            .map((l, idx) => (idx === 0 ? `> [!note] ${l}` : `> ${l}`))
            .join("\n");
        case "divider":
          return "---";
        case "image":
          return `![${block.alt || ""}](taistu-file:${block.fileId || ""})`;
        default:
          return block.text || "";
      }
    })
    .join("\n");
}

/** Flatten note blocks into visual editor rows (top-to-bottom reading order). */
export function flattenNoteBodyToVisualLines(blocks) {
  const lines = [];
  let numberedCounter = 0;

  for (const block of blocks || []) {
    if (block.type !== "numbered") numberedCounter = 0;

    if (isEmptyBlock(block)) {
      if (block.type === "divider") lines.push({ kind: "divider" });
      continue;
    }

    switch (block.type) {
      case "h1":
        lines.push({ kind: "h1", text: block.text || "" });
        break;
      case "h2":
        lines.push({ kind: "h2", text: block.text || "" });
        break;
      case "h3":
        lines.push({ kind: "h3", text: block.text || "" });
        break;
      case "h4":
        lines.push({ kind: "h4", text: block.text || "" });
        break;
      case "bullet":
        lines.push({ kind: "bullet", text: block.text || "" });
        break;
      case "numbered":
        numberedCounter += 1;
        lines.push({ kind: "numbered", text: block.text || "", number: numberedCounter });
        break;
      case "todo":
        lines.push({ kind: "todo", text: block.text || "", checked: Boolean(block.checked) });
        break;
      case "toggle":
        if (block.title?.trim()) lines.push({ kind: "toggle-title", text: block.title });
        if (block.body) {
          block.body.split("\n").forEach((text) => lines.push({ kind: "toggle-body", text }));
        }
        break;
      case "quote":
        (block.text || "").split("\n").forEach((text) => lines.push({ kind: "quote", text }));
        break;
      case "callout":
        (block.text || "").split("\n").forEach((text) => lines.push({ kind: "callout", text }));
        break;
      case "divider":
        lines.push({ kind: "divider" });
        break;
      case "image":
        lines.push({ kind: "image", text: block.alt || "Image" });
        break;
      default:
        lines.push({ kind: "paragraph", text: block.text || "" });
        break;
    }
  }

  return lines;
}

/** First N visual lines of the full note body. */
export function notePreviewVisualLines(content, maxLines = 3) {
  return flattenNoteBodyToVisualLines(parseNoteContent(content)).slice(0, maxLines);
}

function stripInlineMarkdown(text) {
  return String(text || "")
    .replace(/\*\*(.+?)\*\*/g, "$1")
    .replace(/(?<!\*)\*(?!\*)(.+?)(?<!\*)\*(?!\*)/g, "$1")
    .trim();
}

export function notePreviewFromContent(content) {
  const blocks = parseNoteContent(content);

  for (const block of blocks) {
    if (block.type === "divider") break;
    if (isEmptyBlock(block)) continue;
    const text = stripInlineMarkdown(blockInlineText(block));
    if (text) return text;
  }

  return "No body text yet.";
}
